import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  isEmpty,
  get,
  isEqual,
  cloneDeep,
  filter,
  differenceBy,
  isString,
  omit,
  startCase,
  toLower,
  orderBy,
} from 'lodash';
import produce from 'immer';
import { CSSTransition } from 'react-transition-group';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { constants } from '../../constants/configuration';
import routes from '../../routes';
import {
  fetchViewCartItemsAction,
  placeOrderAction,
  clearApiErrorResponseAction,
  clearViewCartItems,
  updateProfileAction,
  dispatchPreviousSelectedTableAction,
  fetchSelectedTableAction,
  dispatchSelectedAddressAction,
  dispatchUserCartAction,
  fetchCartUpsellItemsAction,
  updateCartUpsellItemAction,
  fetchGuestCashCardsAction,
  beginAjaxCallAction,
  endAjaxCallAction,
  createCashCardRechargeAction,
} from '../../actions/app.action';
import {
  signInByTrueCallerAction,
  dispatchGuestLoginDataAction,
} from '../../actions/authentication';
import {
  getTableLabel,
  getCurrentRestaurantCode,
  toTitleCase,
  generateUUID,
  isIOS,
} from '../../utils/helpers';
import {
  paymentTypes,
  upsellStackMountTransitionDelay,
  apiResponseCodes,
  exceptionComponentPageTypes,
  customErrorCodes,
  loyaltyTypesConfig,
  cashCardPaymentCombinationsConfig,
  authInitiatorFeatures,
  toastMessageTime,
  onlinePaymentTypesConfig,
  upsellSearchErrorCase,
  currencies,
  offerTypeCode,
  featureSpecificAPIErrorCodes,
} from '../../constants/globals';
import {
  selectMenuItemsMap,
  selectRestaurant,
  getErrorTitle,
  getErrorDescription,
  getButtonHandlerCta,
  getUpsellErrorPopupContent,
  getTrueCallerDeepLink,
  getTrueCallerErrorContent,
  isMembershipModuleEnabledForRestaurant,
  isMembershipIdMandatoryForRestaurant,
  getMemberIdFromLocalStorage,
} from '../../selectors';

import { SubscriptionContext } from '../../containers';

import UpsellItemCardStack from '../../components/UpsellItemCardStack'; // Needs to be loaded synchronously as the transition component (CSSTransition) needs it immediately at render
import UpsellButton from '../../components/elements/Button/Upsell';
import closeIcon from '../../assets/images/icons/close.svg';
import applyOfferIcon from '../../assets/images/icons/apply-offer.svg';
import infoIcon from '../../assets/images/icons/info-icon.svg';
import editIcon from '../../assets/images/icons/edit.svg';

import './Cart.scss';

const Button = asyncRoute(() => import('../../components/elements/Button'));
const TextField = asyncRoute(() => import('../../components/elements/TextField'));
const CheckBox = asyncRoute(() => import('../../components/elements/CheckBox'));
const Header = asyncRoute(() => import('../../components/Header'));
const FoodItem = asyncRoute(() => import('../../components/FoodItem'));
const AuthenticationPopup = asyncRoute(() => import('../../components/Popup/AuthenticationPopup'));
const PickupTimePopup = asyncRoute(() => import('../../components/Popup/PickupTimePopup'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const UpdateProfileComponent = asyncRoute(() => import('../../components/Popup/UpdateProfile'));
const VehicleDetailsComponent = asyncRoute(() => import('../../components/Popup/VehicleDetails'));
const DeliveryAddressPopup = asyncRoute(() => import('../../components/Popup/DeliveryAddress'));
const DeliveryCard = asyncRoute(() => import('../../components/Card/DeliveryCard'));
const OfferedItemsCard = asyncRoute(() => import('../../components/Card/OfferedItemsCard'));
const AvailedUpsellItemCard = asyncRoute(() => import('../../components/Card/AvailedUpsellItem'));
const PaymentPopup = asyncRoute(() => import('../../components/Popup/Payment'));
const AdditionalChargesPopup = asyncRoute(() => import('../../components/Popup/AdditionalCharges'));
const MembershipPopup = asyncRoute(() => import('../../components/Popup/Membership'));
const Popup = asyncRoute(() => import('../../components/Popup'));
const Toast = asyncRoute(() => import('../../components/elements/Toast'));

const cartScreenConfig = constants.cart;
const upsellConfig = constants.upsell;
const authenticationConfig = constants.authentication;

class Cart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cart: {},
      specialInstructions: get(props, 'cart.specialInstructions', ''),
      showAuthenticationPopup: false,
      showPaymentTypePopup: false,
      phonenumber: '',
      otp: '',
      shouldDisableOtpBtn: true,
      shouldDisableLoginBtn: true,
      shouldDisableOtpTextField: true,
      isOtpSent: false,
      includeOrder: false,
      showErrorPopup: false,
      showUpdateProfilePopup: false,
      showVehicleDetailsPopup: false,
      showPostPaidConfirmationPopup: false,
      showPickUpTimePopup: false,
      showDeliveryAddressPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      disableProceedBtn: false,
      appliedOffers: [],
      isTakeAway: get(props, 'cart.isTakeAway', false),
      selectedPickupTime: null,
      page: 'cart',
      showSelfPickUpSection: false,
      restaurantTableId: '',
      showUpsellItemCardStack: false,
      showUpsellStackPostLogin: false,
      cartUpsellItems: [],
      handleErrorCTA: null,
      errorPopupCancellable: true,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      isShowOffers: false,
      showAdditionalChargesPopup: false,
      isDeliveryCardLinkClicked: false,
      toast: '',
      selectedMenuItem: {},
      removableUpsellItemIds: [],
      popupChildren: '',
      isCartReady: false,
      showMembershipPopup: false,
      membershipPopupContext: '',
    };

    this.handleCta = this.handleCta.bind(this);
    this.handlePickUpTimeChange = this.handlePickUpTimeChange.bind(this);
    this.onClosePickupTimePopup = this.onClosePickupTimePopup.bind(this);
    this.handlePickUpTimeCTA = this.handlePickUpTimeCTA.bind(this);
    this.redirectToOffersScreen = this.redirectToOffersScreen.bind(this);
  }

  static contextType = SubscriptionContext;

  componentDidMount() {
    const { viewCartItems, selectedTable } = this.props;
    const { isShowOffers } = this.state;

    if (!isEmpty(viewCartItems)) {
      const orderOffersArr = get(viewCartItems, 'orderOffers', []);
      const appliedOffers = this.getAppliedOffer({ orderOffersArr });
      this.setState(
        {
          cart: viewCartItems,
          appliedOffers,
          restaurantTableId: get(selectedTable, 'id', ''),
        },
        () => {
          this.props.actions
            .fetchViewCartItemsAction({
              ...viewCartItems,
              appliedOffers,
              isShowOffers,
            })
            .then((response) => {
              this.setIsCartReady(true);
            });
        },
      );
    } else {
      // If viewCart state in store does not receive any data from backend due to contextless URL hits to cart screen, show error
      this.setState({
        showErrorPopup: true,
        showPickUpTimePopup: false,
        showUpdateProfilePopup: false,
        showVehicleDetailsPopup: false,
        showAuthenticationPopup: false,
        errorContent: {
          title: getErrorTitle({ code: customErrorCodes.contextlessURLHit }),
          description: getErrorDescription({ code: customErrorCodes.contextlessURLHit }),
          buttonCta: getButtonHandlerCta({ code: customErrorCodes.contextlessURLHit }),
        },
        handleErrorCTA: this.handleErrorButtonCta({ code: customErrorCodes.contextlessURLHit }),
        errorPopupStyleType: exceptionComponentPageTypes.fullSize,
      });
    }
    if (constants.takeAwayModes.includes(get(selectedTable, 'type', ''))) {
      this.setState({
        isTakeAway: true,
      });
    }

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  componentWillReceiveProps(nextProps) {
    const { cart } = this.state;
    const { restaurantConfig, selectedTable, loginData } = this.props;
    const name = get(loginData, 'name', '');
    const askGuestArrival = get(restaurantConfig, 'askGuestArrival', false);
    if (
      !isEmpty(get(nextProps, 'viewCartItems')) &&
      !isEqual(get(this.props, 'viewCartItems'), get(nextProps, 'viewCartItems'))
    ) {
      if (get(nextProps, 'viewCartItems.items').length > 0) {
        const orderOffersArr = get(nextProps, 'viewCartItems.orderOffers', []);
        const orderOffer =
          orderOffersArr &&
          orderOffersArr.length > 0 &&
          orderOffersArr.find((orderOffer) => orderOffer);
        let appliedOffers = [];
        if (get(orderOffer, 'type', '') === offerTypeCode.ItemFree) {
          get(orderOffer, 'code', '') && appliedOffers.push(orderOffer.code);
        } else {
          appliedOffers =
            get(orderOffer, 'discountAmount', 0) > 0 ? [get(orderOffer, 'offerId', '')] : [];
        }
        this.setState({
          cart: get(nextProps, 'viewCartItems', {}),
          appliedOffers,
        });
      } else {
        this.onBackClick();
      }
    }
    // check if login data is updated or not
    if (
      !isEqual(get(this.props, 'loginData'), get(nextProps, 'loginData')) &&
      !isEmpty(get(nextProps, 'loginData'))
    ) {
      if (!isEmpty(cart)) {
        if (
          !isEmpty(get(nextProps, 'loginData.name', '')) &&
          !isEqual(startCase(toLower(get(nextProps, 'loginData.name', ''))), 'Guest')
        ) {
          // check if name is not empty or doesn't starts with 'Guest' then open update profile popup
          this.setState({
            showAuthenticationPopup: false,
            showUpdateProfilePopup: false,
            disableProceedBtn: false,
          });
        } else if (isEqual(get(selectedTable, 'type'), 'DL')) {
          // check if table type is 'DT'
          // check if guest name is 'Guest' then open update profile popup
          if (isEmpty(name) || isEqual(startCase(toLower(name)), 'Guest')) {
            this.setState({
              showAuthenticationPopup: false,
              showUpdateProfilePopup: true,
            });
          }
        } else if (isEqual(get(selectedTable, 'type'), 'SP') && askGuestArrival) {
          // else if, check table type is 'SP' and askGuestArrival === true
          // check if guest name is 'Guest' then open update profile popup
          if (isEqual(startCase(toLower(name)), 'Guest')) {
            this.setState({
              showAuthenticationPopup: false,
              showUpdateProfilePopup: true,
            });
          } else {
            // else open self pick up time popup
            this.setState({
              showUpdateProfilePopup: false,
              showAuthenticationPopup: false,
              showPickUpTimePopup: true,
            });
          }
        } else if (isEqual(get(selectedTable, 'type'), 'DT')) {
          // check if table type is 'DT'
          // check if guestVehicle Arr is not empty and guest name is not 'Guest' then open vehicle details popup
          if (
            !isEmpty(get(loginData, 'guestVehicles')) &&
            !isEqual(startCase(toLower(name)), 'Guest')
          ) {
            this.setState({
              showAuthenticationPopup: false,
              showVehicleDetailsPopup: true,
            });
          } else {
            // opens update profile popup
            this.setState({
              showAuthenticationPopup: false,
              showUpdateProfilePopup: true,
            });
          }
        } else {
          // TODO: Find out if this logic is of any use
          // this.setState(
          //   {
          //     disableProceedBtn: true,
          //   },
          //   () => {
          //     this.injectMembershipFlowIntoCartRedirection();
          //   },
          // );

          // else redirect to cart redirection
          this.injectMembershipFlowIntoCartRedirection();
        }
      }
    }

    // check for error response
    if (
      !isEmpty(get(nextProps, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(nextProps, 'errorResponse')) &&
      ![featureSpecificAPIErrorCodes.otpInvalid].includes(get(nextProps, 'errorResponse.code'))
    ) {
      const { code, message, data } = nextProps.errorResponse;
      if (isEqual(code, upsellSearchErrorCase.minCartValueError)) {
        const { items, showUpsell } = this.props.viewCartItems;
        const availedUpsellItems = items && items.length > 0 && items.filter((item) => item.itmSrc);
        this.setState(
          {
            showPopup: true, // set generic popup state to true
            popupContent: getUpsellErrorPopupContent(code, routes.containers.cart),
            popupChildren: (
              <div className="cart_popup-children">
                <div className="min-upsell-amount">
                  {`${cartScreenConfig.minimumCartValue} : ${currencies.INR.symbol}${get(
                    showUpsell,
                    'minCartValue',
                  )}`}
                </div>
                <div className="loose-deal">{cartScreenConfig.looseDeal}</div>
                <AvailedUpsellItemCard availedUpsellItems={availedUpsellItems} />
              </div>
            ),
            handlePopupCTA: () => this.onClosePopupComponent(),
            removableUpsellItemIds: get(data, 'upsellItemIds', []),
          },
          () => this.props.actions.clearApiErrorResponseAction(),
        );
      } else {
        this.setState(
          {
            showErrorPopup: true,
            showPickUpTimePopup: false,
            showUpdateProfilePopup: false,
            showVehicleDetailsPopup: false,
            showAuthenticationPopup: false,
            showPaymentTypePopup: false,
            errorContent: {
              title: getErrorTitle({ source: cartScreenConfig, code }),
              description: getErrorDescription({ source: cartScreenConfig, code, message }),
              buttonCta: getButtonHandlerCta({ source: cartScreenConfig, code }),
            },
            handleErrorCTA: this.handleErrorButtonCta({ code }),
          },
          () => this.props.actions.clearApiErrorResponseAction(),
        );
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { toast } = this.state;
    // If the table type has been updated to self pickup, mark the isTakeAway state as true and also dispatch it to cart to maintain consistency
    const prevTableType = get(prevProps, 'selectedTable.type');
    const currentTableType = get(this.props, 'selectedTable.type');

    if (prevTableType !== currentTableType) {
      const { isTakeAway } = this.state;

      if (constants.takeAwayModes.includes(currentTableType)) {
        // If current table type is any takeaway order mode (currently only self pickup)
        // If the order is not already marked for a take away, mark it
        if (!isTakeAway) {
          this.setIsTakeAway(true);
        }
      } else {
        // If current table type is any non-takeaway order mode
        // If the order is not already unmarked for a take away, unmark it
        if (isTakeAway) {
          this.setIsTakeAway(false);
        }
      }
    }

    // Generic toast state
    if (!isEmpty(toast) && !isEqual(toast, prevState.toast)) {
      setTimeout(
        () =>
          this.setState({
            toast: '',
          }),
        toastMessageTime,
      );
    }
  }

  componentWillUnmount() {
    this.props.actions.clearApiErrorResponseAction();
  }

  setIsCartReady = (flag) => {
    this.setState({ isCartReady: flag });
  };

  setIsTakeAway = (flag) => {
    this.setState({ isTakeAway: flag }, () =>
      this.props.actions.dispatchUserCart({ isTakeAway: flag }),
    );
  };

  handleErrorButtonCta = ({ code }) => {
    switch (code) {
      case 'ITEM_NOT_AVAILABLE':
        return this.handleDisableItems;
      case 'TOO_MANY_ORDERS':
        return this.handleCta;
      case 'UPSELL_SESSION_TIMEOUT':
        return this.handleRemoveAvailedUpsellItem;
      case customErrorCodes.contextlessURLHit:
        return this.handleContextlessURLHitErrorPopupCTA;
      default:
        return this.handleBackToMenuCTA;
    }
  };

  injectMembershipFlowIntoCartRedirection = () => {
    const memberId = this.getMemberId();

    if (
      isMembershipModuleEnabledForRestaurant(this.props.restaurantConfigAdditionalProps) &&
      !memberId
    ) {
      this.openMembershipPopup();
    } else {
      this.handleCartRedirection();
    }
  };

  handleCartRedirection = () => {
    let appliedOffers = [];
    const {
      restaurantConfig,
      viewCartItems,
      loginData,
      selectedAddress,
      selectedTable,
      cartUpsellItems,
    } = this.props;
    const { cart, restaurantTableId } = this.state;
    const { name, id: guestId } = loginData;
    const { paymentType } = restaurantConfig;
    const selectedTableType = get(selectedTable, 'type', '');
    const availablePaymentTypes = get(selectedTable, 'availablePaymentTypes', []);
    const orderOffersArr = get(viewCartItems, 'orderOffers', []);
    const orderOffer =
      orderOffersArr &&
      orderOffersArr.length > 0 &&
      orderOffersArr.find((orderOffer) => orderOffer);
    let appliedOfferId = get(orderOffer, 'offerId', '');
    let discountAmount = get(orderOffer, 'discountAmount', 0);
    selectedAddress && delete selectedAddress.id;
    const offerType = get(orderOffer, 'type', '');

    let draftCartState = {};
    draftCartState = produce(cart, (draft) => {
      if (
        draft &&
        isEqual(selectedTableType, 'DL') &&
        !isEmpty(get(draft, 'deliveryLocation')) &&
        !isEmpty(selectedAddress)
      )
        draft['deliveryLocation'].guestAddress = selectedAddress;
    });

    if (!isEmpty(appliedOfferId)) {
      if (offerType === offerTypeCode.ItemFree) {
        appliedOffers.push(appliedOfferId);
      } else {
        discountAmount > 0 && appliedOffers.push(appliedOfferId);
      }
    }

    const upsellSessionId = get(cartUpsellItems, 'id', '');
    if (isEmpty(name) || isEqual(startCase(toLower(name)), 'Guest')) {
      // check if name is empty or starts with 'Guest' then open update profile popup
      this.setState({
        showAuthenticationPopup: false,
        showPaymentTypePopup: false,
        showVehicleDetailsPopup: false,
        showUpdateProfilePopup: true,
      });
    } else if (isEmpty(selectedAddress) && isEqual(selectedTableType, 'DL')) {
      this.setState({
        showDeliveryAddressPopup: true,
        showSelfPickUpSection: true,
      });
    } else {
      if (availablePaymentTypes && availablePaymentTypes.length > 1) {
        // check if availablePaymentTypes.length > 1 then open payment type popup
        this.setState({
          showAuthenticationPopup: false,
          showPaymentTypePopup: true,
          showUpdateProfilePopup: false,
          showVehicleDetailsPopup: false,
        });
      } else if (availablePaymentTypes && availablePaymentTypes.length === 1) {
        // else if, check availablePaymentTypes.length === 1 then placed order based on paymentType
        let paymentType = availablePaymentTypes[0];
        if (
          isEqual(paymentType, paymentTypes.POSTPAYMENT) ||
          isEqual(paymentType, paymentTypes.PREPAYMENT)
        ) {
          this.setState({
            showPostPaidConfirmationPopup: true,
            showPaymentTypePopup: true,
            showAuthenticationPopup: false,
            showVehicleDetailsPopup: false,
            showUpdateProfilePopup: false,
          });
        } else {
          this.setState(
            {
              showAuthenticationPopup: false,
              showPaymentTypePopup: false,
              showUpdateProfilePopup: false,
              showVehicleDetailsPopup: false,
            },
            () =>
              this.placeOrder(
                {
                  ...draftCartState,
                  restaurantTableId,
                  guestId,
                  paymentType,
                  appliedOffers,
                  upsellSessionId,
                },
                paymentType,
              ),
          );
        }
      } else {
        if (isEqual(paymentType, paymentTypes.POSTPAYMENT)) {
          this.setState({
            showPostPaidConfirmationPopup: true,
            showPaymentTypePopup: true,
            showAuthenticationPopup: false,
            showVehicleDetailsPopup: false,
            showUpdateProfilePopup: false,
          });
        } else {
          this.setState(
            {
              showAuthenticationPopup: false,
              showPaymentTypePopup: false,
              showUpdateProfilePopup: false,
              showVehicleDetailsPopup: false,
            },
            () =>
              this.placeOrder(
                {
                  ...draftCartState,
                  restaurantTableId,
                  guestId,
                  paymentType,
                  appliedOffers,
                  upsellSessionId,
                },
                paymentType,
              ),
          );
        }
      }
    }
  };

  /**
   * Payment handler on cart
   * @param {String} metaPaymentType Custom payment types which can be various combinations of payments
   * @param {Object} cashCardTransactionProps Cash card transaction-related (payment/recharge) properties
   */
  handlePaymentTypeClick = async (
    metaPaymentType,
    cashCardTransactionProps = {},
    isRechargeAndOrderFlow,
  ) => {
    const { cart, restaurantTableId } = this.state;
    const { viewCartItems, loginData, selectedTable, cartUpsellItems } = this.props;
    const guestId = get(loginData, 'id', '');

    // Prepare applied offers
    let appliedOffers = [];
    const orderOffersArr = get(viewCartItems, 'orderOffers', []);
    const orderOffer =
      orderOffersArr &&
      orderOffersArr.length > 0 &&
      orderOffersArr.find((orderOffer) => orderOffer);
    let appliedOfferId = get(orderOffer, 'offerId', '');
    let discountAmount = get(orderOffer, 'discountAmount', 0);
    const offerType = get(orderOffer, 'type', '');

    if (!isEmpty(appliedOfferId)) {
      if (offerType === offerTypeCode.ItemFree) {
        appliedOffers.push(appliedOfferId);
      } else {
        discountAmount > 0 && appliedOffers.push(appliedOfferId);
      }
    }

    const upsellSessionId = get(cartUpsellItems, 'id', '');
    // Remove id from guest address in delivery location object as per /orders API contract
    let draftCartState = {};
    const selectedTableType = get(selectedTable, 'type', '');
    draftCartState = produce(cart, (draft) => {
      if (
        draft &&
        isEqual(selectedTableType, 'DL') &&
        !isEmpty(get(draft, 'deliveryLocation.guestAddress.id'))
      ) {
        delete draft['deliveryLocation'].guestAddress.id;
      }
    });

    // Prepare loyalty points payments list
    const loyaltyPointsPayments = [];
    const isCashCardPayment =
      !isRechargeAndOrderFlow && get(cashCardTransactionProps, 'amount') > 0; // If cash card deductible amount in cash card transaction properties passed to this handler is non-zero and it is not a recharge and order flow, that implies this is a cash card payment
    if (isCashCardPayment) {
      const cashCardPaymentObject = this.prepareLoyaltyPointsPaymentObject({
        loyaltyType: loyaltyTypesConfig.cashCard,
        cashCardTransactionProps,
      });

      loyaltyPointsPayments.push(cashCardPaymentObject);
    }

    await this.placeOrder(
      {
        ...draftCartState,
        restaurantTableId,
        guestId,
        paymentType: this.preparePaymentTypeForAPI(metaPaymentType), // Send payment type within the create order API request body as per the API defined criteria (See FUDR-1798)
        ...(isCashCardPayment ? { pointsPayments: loyaltyPointsPayments } : {}), // Send loyalty points payments list if it's a cash card payment
        appliedOffers,
        upsellSessionId,
      },
      metaPaymentType,
      isRechargeAndOrderFlow,
      get(cashCardTransactionProps, 'amount'), // recharge amount in case it's a recharge and order flow
    );
  };

  placeOrder = async (
    placeOrderAPIRequestBody,
    metaPaymentType,
    isRechargeAndOrderFlow,
    rechargeAmount,
  ) => {
    const memberId = this.getMemberId();

    const draftOrderRequest = {
      ...placeOrderAPIRequestBody,
      ...(!!memberId ? { additionalData: { memberId } } : {}),
    }; // Inject member id from localstorage
    const placeOrderResponse = await this.props.actions.placeOrderAction(draftOrderRequest);

    const orderId = get(placeOrderResponse, 'data.id', '');

    if (!apiResponseCodes.SUCCESS.includes(get(placeOrderResponse, 'status'))) {
      return placeOrderResponse;
    }

    // If it's not a cash card recharge and order payment flow, proceed to payment directly, otherwise, initiate a cash card recharge as well and send the linked order id with it and then proceed to payment
    if (!isRechargeAndOrderFlow) {
      this.navigateAsPerPaymentType(metaPaymentType, orderId);
    } else {
      this.proceedWithRecharge(rechargeAmount, orderId);
    }
  };

  proceedWithRecharge = async (rechargeAmount, orderId) => {
    const {
      guestCashCards,
      restaurant,
      loginData,
      accessToken,
      actions: { createCashCardRecharge },
      history,
      location,
    } = this.props;

    const [guestCashCardObject] = guestCashCards; // On cart, we bring singular guest cash card of the visited outlet

    const guestCashCardId = get(guestCashCardObject, 'id', '');
    let cashCardId = get(guestCashCardObject, 'cashCard.id', '');

    const visitedRestaurantId = get(restaurant, 'id', '');
    let restaurantId = visitedRestaurantId;

    const cashCardRestaurants = get(guestCashCardObject, 'cashCard.cashCardRestaurants', []);
    const cashCardRestaurantObject =
      cashCardRestaurants.find((restaurant) => get(restaurant, 'isCardAdmin') === true) || {}; // restaurantId field which is required by the API for reconciliation process is supposed to be that of the cash card restaurant group's admin
    restaurantId = get(cashCardRestaurantObject, 'restaurantId', visitedRestaurantId);

    const guestId = get(loginData, 'id', '');

    const rechargeRequestBody = {
      guestCashCardId,
      cashCardId,
      guestId,
      restaurantId,
      amount: rechargeAmount,
      linkedPaymentId: orderId,
    };

    // Create cash card DRAFT recharge
    const createCashCardRechargeResponse = await createCashCardRecharge(
      accessToken,
      rechargeRequestBody,
    );

    // Navigate to the webapp's payment route if the recharge DRAFT was created successfully
    if (apiResponseCodes.SUCCESS.includes(get(createCashCardRechargeResponse, 'status'))) {
      const currentRoute = location ? location.pathname : '';

      history &&
        history.push(`/${routes.containers.payment}`, {
          onlinePaymentType: onlinePaymentTypesConfig.orderPaymentPlusCashCardRecharge.code,
          initiatorRoute: currentRoute,
        });
    }
  };

  navigateAsPerPaymentType = (metaPaymentType, orderId) => {
    const { history, match } = this.props;

    if (isEmpty(history)) return;

    const currentRoute = match.path;

    switch (metaPaymentType) {
      case paymentTypes.PREPAYMENT:
      case cashCardPaymentCombinationsConfig.cashCardPlusPrePaid:
        history.push(`/${routes.containers.payment}`, {
          onlinePaymentType: onlinePaymentTypesConfig.orderPayment.code,
          initiatorRoute: currentRoute,
        });
        return;
      case paymentTypes.POSTPAYMENT:
      case cashCardPaymentCombinationsConfig.cashCardOnly:
      case cashCardPaymentCombinationsConfig.cashCardPlusPostPaid:
        history.push(`/${routes.containers.confirmation}/${orderId}`, {
          initiatorRoute: `/${routes.containers.confirmation}`,
        }); // Set initiator route as confirmation itself as '/cart' being a contextful route, cannot serve as the initiator for confirmation
        return;
      default:
        return;
    }
  };

  prepareLoyaltyPointsPaymentObject = ({ loyaltyType, cashCardTransactionProps }) => {
    let loyaltyPointsPaymentObject = { loyaltyType }; // Properties common for any loyalty type payment can be inserted on initialization

    // Insert conditional properties based on loyalty type
    switch (loyaltyType) {
      case loyaltyTypesConfig.cashCard:
        return { ...loyaltyPointsPaymentObject, ...cashCardTransactionProps };
      default:
        return loyaltyPointsPaymentObject;
    }
  };

  // Prepare payment type for the create order API request body as per the API defined criteria (See FUDR-1798)
  preparePaymentTypeForAPI = (metaPaymentType) => {
    switch (metaPaymentType) {
      case cashCardPaymentCombinationsConfig.cashCardOnly:
      case cashCardPaymentCombinationsConfig.cashCardPlusPrePaid:
      case cashCardPaymentCombinationsConfig.cashCardRechargeAndOrderPayment:
        return paymentTypes.PREPAYMENT;
      case cashCardPaymentCombinationsConfig.cashCardPlusPostPaid:
        return paymentTypes.POSTPAYMENT;
      default:
        return metaPaymentType;
    }
  };

  getSelectedVehicle = (id) => {
    let deliveryLocation = {};
    let updatedCart = {};
    const { loginData } = this.props;
    const { guestVehicles } = loginData;
    const { cart } = this.state;
    if (isString(id)) {
      deliveryLocation.guestVehicle =
        guestVehicles && guestVehicles.find((vehicleObj) => vehicleObj.id === id);
    } else {
      let vehicleObj = omit(id, ['name', 'email', 'isAcceptTnc']);
      deliveryLocation.guestVehicle = vehicleObj;
    }
    updatedCart = { ...cart, deliveryLocation };
    // After selecting vehicle, redirect to cart
    this.setState(
      {
        showUpdateProfilePopup: false,
        cart: updatedCart,
      },
      this.injectMembershipFlowIntoCartRedirection(),
    );
  };

  handleChange = (e, { value }) => this.setState({ value });

  onBackClick = () => {
    const { history, selectedTable, restaurant } = this.props;
    const scanCode = get(selectedTable, 'scanCode', '');
    // const restaurantCode = get(restaurant, 'restaurantCode', '');
    // const tableNumber = get(selectedTable, 'tableNumber', '');
    // let currentRestaurantCode = getCurrentRestaurantCode({ restaurantCode, tableNumber });
    history.push(`/${routes.containers.restaurant}/${scanCode}`);
  };

  onHide = () =>
    this.setState({
      visible: false,
    });

  handleItemClick = (e, { name }) => this.setState({ activeItem: name });

  onProceedClick = () => {
    const { cart, isTakeAway, specialInstructions } = this.state;
    const { restaurantConfig, selectedTable, loginData } = this.props;
    const name = get(loginData, 'name', '');
    const selectedTableType = get(selectedTable, 'type', '');
    const askGuestArrival = get(restaurantConfig, 'askGuestArrival', false);
    const draftCart = produce(cart, (draftState) => {
      if (isTakeAway) {
        if (!isEmpty(specialInstructions)) {
          draftState.customization = `${cartScreenConfig.takeAway}, ${specialInstructions}`;
        } else {
          draftState.customization = cartScreenConfig.takeAway;
        }
      } else {
        if (!isEmpty(specialInstructions)) {
          draftState.customization = specialInstructions;
        }
      }
    });
    if (!isEmpty(loginData)) {
      if (
        isEqual(selectedTableType, 'DT') &&
        !(isEmpty(name) || isEqual(startCase(toLower(name)), 'Guest'))
      ) {
        this.setState({
          showAuthenticationPopup: false,
          showPaymentTypePopup: false,
          showUpdateProfilePopup: false,
          showVehicleDetailsPopup: true,
          cart: draftCart,
        });
      } else if (
        isEqual(selectedTableType, 'SP') &&
        askGuestArrival &&
        !(isEmpty(name) || isEqual(startCase(toLower(name)), 'Guest'))
      ) {
        this.setState({
          showAuthenticationPopup: false,
          showPickUpTimePopup: true,
          cart: draftCart,
        });
      } else {
        this.setState(
          {
            cart: draftCart,
          },
          () => {
            this.injectMembershipFlowIntoCartRedirection();
          },
        );
      }
    } else {
      this.setState(
        {
          cart: draftCart,
        },
        () => {
          this.handleLoginClick();
        },
      );
    }
  };

  onCloseAuthenticationPopup() {
    this.setState({
      showAuthenticationPopup: false,
    });
  }

  onClosePaymentTypePopup = () => {
    this.setState({
      showPaymentTypePopup: false,
      showPostPaidConfirmationPopup: false,
    });
  };

  onFoodAddClick = (selectedItem) => {
    const { cart } = this.state;
    const selectedIndex = get(selectedItem, 'index', 0);
    const orderOffersArr = get(cart, 'orderOffers', []);
    const draftCart = produce(cart, (draftState) => {
      if (selectedIndex > -1) {
        ++draftState.items[selectedIndex].quantity;
        draftState['appliedOffers'] = this.getAppliedOffer({ orderOffersArr });
      }
    });
    this.setState(
      {
        selectedMenuItem: selectedItem,
      },
      () => {
        this.props.actions.fetchViewCartItemsAction(draftCart).then((response) => {
          this.setIsCartReady(true);
        });
      },
    );
  };

  onFoodRemoveClick = (selectedItem) => {
    const { cart } = this.state;
    const selectedIndex = get(selectedItem, 'index', 0);
    const orderOffersArr = get(cart, 'orderOffers', []);
    const draftCart = produce(cart, (draftState) => {
      if (selectedIndex > -1) {
        --draftState.items[selectedIndex].quantity;
        if (draftState.items[selectedIndex].quantity === 0) {
          draftState.items.splice(selectedIndex, 1);
        }
        draftState['appliedOffers'] = this.getAppliedOffer({ orderOffersArr });
      }
    });
    this.setState(
      {
        selectedMenuItem: selectedItem,
      },
      () => {
        this.props.actions.fetchViewCartItemsAction(draftCart).then((response) => {
          this.setIsCartReady(true);
        });
      },
    );
  };

  renderCartItems = (cart) => {
    let cartitems = '';
    let { items } = cart;
    items = items && items.length > 0 && items.filter((item) => !item.itmSrc); // temporary filtering out the items other than the upsell items

    cartitems =
      items &&
      items.map((item, index) => (
        <FoodItem
          key={`${get(item, 'menuItemId', '')}~${index}`}
          name={get(item, 'menuItemName', '')}
          price={get(item, 'effectivePrice', 0)}
          description={get(item, 'description', '')}
          img={get(item, 'img', '')}
          count={get(item, 'quantity', 0)}
          menuItem={{ ...item, index }}
          type={get(item, 'type', '')}
          isEnabled
          onFoodAddClick={this.onFoodAddClick}
          onFoodRemoveClick={this.onFoodRemoveClick}
          customizedItems={get(item, 'orderCustomizations', [])}
          infoType={false}
        />
      ));

    return cartitems;
  };

  handleCta = () => {
    const { history, selectedTable, restaurant } = this.props;
    const restaurantCode = get(restaurant, 'restaurantCode', '');
    const tableNumber = get(selectedTable, 'tableNumber', '');
    let currentRestaurantCode = getCurrentRestaurantCode({ restaurantCode, tableNumber });
    this.setState(
      {
        showErrorPopup: false,
      },
      () => {
        this.props.actions.clearViewCartItems().then(() => {
          history.push(currentRestaurantCode);
        });
      },
    );
  };

  handleDisableItems = () => {
    const { disabledItems, loginData } = this.props;
    const { cart } = this.state;
    const { items } = cart;
    let itemsArr = cloneDeep(items);
    let disabledItemsArr = filter(itemsArr, (item) => disabledItems.includes(item.menuItemId));
    let remainingItemsArr = differenceBy(items, disabledItemsArr, 'menuItemId');
    const guestId = get(loginData, 'id', '');
    const orderOffersArr = get(cart, 'orderOffers', []);
    const orderOffer =
      orderOffersArr &&
      orderOffersArr.length > 0 &&
      orderOffersArr.find((orderOffer) => orderOffer);
    const draftCart = produce(cart, (draftState) => {
      draftState.items = remainingItemsArr;
    });
    this.setState(
      {
        cart: draftCart,
        showErrorPopup: false,
      },
      () => {
        this.props.actions
          .fetchViewCartItemsAction({
            ...draftCart,
            appliedOffers:
              get(orderOffer, 'discountAmount', 0) > 0 ? [get(orderOffer, 'offerId', '')] : [],
            guestId,
          })
          .then((response) => {
            this.setIsCartReady(true);
          });
      },
    );
  };

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  handleBackToMenuCTA = () => {
    const { selectedTable } = this.props;
    this.setState({ showErrorPopup: false }, () => {
      // Reload and open webapp's restaurant menu screen
      window.location.replace(
        `${window.location.origin}/${routes.containers.restaurant}/${get(
          selectedTable,
          'scanCode',
          '',
        )}`,
      );
    });
  };

  onCloseExceptionComponent = () => {
    this.setState({
      showErrorPopup: false,
    });
  };

  onCloseVehicleDetailsPopup = () => {
    this.setState({
      disableProceedBtn: false,
      showVehicleDetailsPopup: false,
    });
  };

  onCloseUpdateProfilePopup = () => {
    this.setState({
      disableProceedBtn: false,
      showUpdateProfilePopup: false,
    });
  };

  onCloseOrderConfirmationPopup = () => {
    this.setState({
      showPostPaidConfirmationPopup: false,
    });
  };

  redirectToOffersScreen = () => {
    this.props.history.push(routes.containers.offers);
  };

  removeAppliedCouponClick = () => {
    let appliedOffers = [];
    const { viewCartItems, loginData } = this.props;
    const guestId = get(loginData, 'id', '');
    this.props.actions
      .fetchViewCartItemsAction({ ...viewCartItems, appliedOffers, guestId })
      .then((response) => {
        this.setIsCartReady(true);
      });
  };

  handleSpecialInstructionsChange = (event) => {
    const { value } = event.target;
    this.setState({
      specialInstructions: value,
    });
  };

  handleSpecialInstructionOnBlur = () => {
    const { cart, specialInstructions, isTakeAway } = this.state;
    this.props.actions.dispatchUserCart({ ...cart, specialInstructions, isTakeAway });
  };

  onTakeAwayClick = (event, { checked }) => {
    let { cart, isTakeAway, specialInstructions } = this.state;
    isTakeAway = checked;
    this.setState(
      {
        isTakeAway,
      },
      () => this.props.actions.dispatchUserCart({ ...cart, isTakeAway, specialInstructions }),
    );
  };

  handlePickUpTimeChange = (event, { value }) => {
    const { cart } = this.state;
    const draftCart = produce(cart, (draftState) => {
      draftState.ePickupTime = value;
    });
    this.setState({
      cart: draftCart,
      selectedPickupTime: value,
    });
  };

  onClosePickupTimePopup = () => {
    this.setState({
      showPickUpTimePopup: false,
    });
  };

  handlePickUpTimeCTA = () => {
    this.setState(
      {
        showPickUpTimePopup: false,
      },
      () => {
        this.injectMembershipFlowIntoCartRedirection();
      },
    );
  };

  handleLinkClick = () => {
    this.setState({
      showDeliveryAddressPopup: true,
      showSelfPickUpSection: true,
      isDeliveryCardLinkClicked: true,
    });
  };

  handleGetItDeliveredClick = () => {
    this.setState({
      showDeliveryAddressPopup: true,
      showSelfPickUpSection: false,
      isDeliveryCardLinkClicked: true,
    });
  };

  onCloseDeliveryAddressPopup = () => {
    this.setState({
      showDeliveryAddressPopup: false,
    });
  };

  handleDeliveryAddressCTA = ({ addressObj, isSelfPickUpSelected, isSavedAddressSelected }) => {
    let appliedOffers = [];
    let deliveryLocation = {};
    const {
      selectedTable,
      serviceTypes,
      loginData,
      selectedAddress,
      viewCartItems,
      verifiedLocalities,
    } = this.props;
    const { cart, isDeliveryCardLinkClicked, specialInstructions } = this.state;
    const guestId = get(loginData, 'id', '');
    const selectedTableType = isSelfPickUpSelected ? 'SP' : 'DL';
    const locationId =
      serviceTypes &&
      get(
        serviceTypes.find((sType) => sType.code === selectedTableType),
        'locationId',
        get(selectedTable, 'id', ''),
      ); // pass default as selected table id, if the same in not available in serviceTypes arr,
    const restaurantTableId = locationId;
    const orderOffersArr = get(viewCartItems, 'orderOffers', []);
    const orderOffer =
      orderOffersArr &&
      orderOffersArr.length > 0 &&
      orderOffersArr.find((orderOffer) => orderOffer);
    let appliedOfferId = get(orderOffer, 'offerId', '');
    let discountAmount = get(orderOffer, 'discountAmount', 0);
    !isEmpty(appliedOfferId) && discountAmount > 0 && appliedOffers.push(appliedOfferId);
    let updateProfilereqBody = {
      guestAddresses: [addressObj],
    };
    if (isEqual(get(selectedTable, 'type'), 'DL')) {
      deliveryLocation.guestAddress = isEmpty(addressObj) ? selectedAddress : addressObj;
    }
    if (isSelfPickUpSelected && !isEmpty(locationId)) {
      const draftCart = produce(cart, (draftState) => {
        draftState.deliveryLocation && delete draftState.deliveryLocation;
        draftState.customization = `${cartScreenConfig.takeAway}${
          !isEmpty(specialInstructions) ? `, ${specialInstructions}` : ''
        }`;
      });

      this.setState(
        {
          showPickUpTimePopup: true,
          showDeliveryAddressPopup: false,
          restaurantTableId,
        },
        () => {
          this.props.actions.dispatchPreviousSelectedTable(selectedTable).then(() => {
            this.props.actions.fetchSelectedTable(locationId);
            this.props.actions
              .fetchViewCartItemsAction({
                ...draftCart,
                restaurantTableId,
                appliedOffers,
                guestId,
              })
              .then((response) => {
                this.setIsCartReady(true);
              });
          });
        },
      );
    } else {
      this.setState(
        {
          showDeliveryAddressPopup: false,
          restaurantTableId,
        },
        () => {
          if (!isEmpty(loginData)) {
            if (isSavedAddressSelected) {
              // check if selfpickup mode is available
              if (!isEmpty(locationId)) {
                this.props.actions.fetchSelectedTable(locationId).then(() => {
                  this.props.actions.dispatchSelectedAddress(addressObj).then(() => {
                    this.props.actions
                      .fetchViewCartItemsAction({
                        ...cart,
                        restaurantTableId,
                        deliveryLocation,
                        appliedOffers,
                        guestId,
                      })
                      .then(() => {
                        if (isDeliveryCardLinkClicked) {
                          this.setState({ isDeliveryCardLinkClicked: false });
                        } else {
                          this.injectMembershipFlowIntoCartRedirection();
                        }

                        this.setIsCartReady(true);
                      });
                  });
                });
              } else {
                this.props.actions.dispatchSelectedAddress(addressObj).then(() => {
                  this.props.actions
                    .fetchViewCartItemsAction({
                      ...cart,
                      restaurantTableId,
                      deliveryLocation,
                      appliedOffers,
                      guestId,
                    })
                    .then(() => {
                      if (isDeliveryCardLinkClicked) {
                        this.setState({ isDeliveryCardLinkClicked: false });
                      } else {
                        this.injectMembershipFlowIntoCartRedirection();
                      }

                      this.setIsCartReady(true);
                    });
                });
              }
            } else {
              const localityName = !isEmpty(get(addressObj, 'localityName', ''))
                ? get(addressObj, 'localityName', '')
                : verifiedLocalities &&
                  verifiedLocalities.length > 0 &&
                  get(
                    verifiedLocalities.find((locality) => locality),
                    'localityName',
                    '',
                  );
              // find locality obj from verifiedLocalities arr
              // check if selfpickup mode is available

              if (!isEmpty(locationId)) {
                this.props.actions.fetchSelectedTable(locationId).then(() => {
                  this.props.actions
                    .dispatchSelectedAddress({ ...addressObj, localityName })
                    .then(() => {
                      this.props.actions.updateProfileAction(updateProfilereqBody, guestId);
                    })
                    .then(() => {
                      const draftCart = produce(cart, (draftState) => {
                        draftState.deliveryLocation = {
                          guestAddress: { ...addressObj, localityName },
                        };
                        draftState.customization && delete draftState.customization;
                      });
                      this.props.actions
                        .fetchViewCartItemsAction({
                          ...draftCart,
                          restaurantTableId,
                          appliedOffers,
                          guestId,
                        })
                        .then(() => {
                          if (isDeliveryCardLinkClicked) {
                            this.setState({ isDeliveryCardLinkClicked: false });
                          } else {
                            this.injectMembershipFlowIntoCartRedirection();
                          }

                          this.setIsCartReady(true);
                        });
                    });
                });
              } else {
                this.props.actions
                  .dispatchSelectedAddress({ ...addressObj, localityName })
                  .then(() => {
                    this.props.actions.updateProfileAction(updateProfilereqBody, guestId);
                  })
                  .then(() => {
                    if (isDeliveryCardLinkClicked) {
                      this.setState({ isDeliveryCardLinkClicked: false });
                    } else {
                      this.injectMembershipFlowIntoCartRedirection();
                    }
                  });
              }
            }
          } else {
            // check if selfpickup mode is available
            if (!isEmpty(locationId)) {
              this.props.actions.fetchSelectedTable(locationId).then(() => {
                this.props.actions.dispatchSelectedAddress(addressObj).then(() => {
                  this.props.actions
                    .fetchViewCartItemsAction({
                      ...cart,
                      restaurantTableId,
                      deliveryLocation,
                      appliedOffers,
                      guestId,
                    })
                    .then(() => {
                      if (isDeliveryCardLinkClicked) {
                        this.setState({ isDeliveryCardLinkClicked: false });
                      } else {
                        this.injectMembershipFlowIntoCartRedirection();
                      }

                      this.setIsCartReady(true);
                    });
                });
              });
            }
          }
        },
      );
    }
  };

  handleUpsellButtonClick = () => {
    const { viewCartItems, loginData } = this.props;
    const guestId = get(loginData, 'id', '');
    // Allow user to view upsell items only if he is logged in, else show login prompt
    if (isEmpty(loginData)) {
      this.handleLoginClick(authInitiatorFeatures.upsell);
    } else {
      // Show upsell item card stack
      this.props.actions
        .fetchCartUpsellItems({ ...viewCartItems, guestId })
        .then((cartUpsellItemsResponse) => {
          if (apiResponseCodes.SUCCESS.includes(get(cartUpsellItemsResponse, 'status', ''))) {
            if (get(cartUpsellItemsResponse, 'data.errorInfo.isError', false)) {
              this.setState({
                showPopup: true,
                popupContent: getUpsellErrorPopupContent(
                  get(cartUpsellItemsResponse, 'data.errorInfo.code', ''),
                ),
                popupChildren: isEqual(
                  get(cartUpsellItemsResponse, 'data.errorInfo.code', ''),
                  upsellSearchErrorCase.minCartValueError,
                ) ? (
                  <div className="cart_popup-children">
                    <div className="min-upsell-amount">
                      {`${cartScreenConfig.minimumCartValue} : ${currencies.INR.symbol}${get(
                        viewCartItems,
                        'showUpsell.minCartValue',
                      )}`}
                    </div>
                  </div>
                ) : (
                  ''
                ),
                handlePopupCTA: () => this.onClosePopupComponent(),
              });
            } else {
              this.setState({
                showUpsellItemCardStack: true,
                cartUpsellItems: orderBy(
                  get(cartUpsellItemsResponse, 'data.upsellItems', []),
                  'sequenceNumber',
                ), // fetch upsellItems from response and order by as per the sequenceNumber
              });
            }
          }
        });
    }
  };

  handleUpsellItemAddToCartCTAClick = async (item) => {
    const { cartUpsellItems } = this.props;
    const { cart } = this.state;
    const itemId = get(item, 'id', '');
    const updateCartUpsellItemResponse = await this.props.actions.updateCartUpsellItem(
      this.createUpdateCartUpsellItemRequestBody({ cartUpsellItems, itemId }),
    );
    const upsellSessionId = get(cartUpsellItems, 'id', '');
    if (apiResponseCodes.SUCCESS.includes(get(updateCartUpsellItemResponse, 'status', ''))) {
      let upsellMenuItemObj = item && item.upsellMenuItems.find((upsellMenuItem) => upsellMenuItem);
      const { upsellItemId, id: upsellMenuItemId, menuItem } = upsellMenuItemObj;
      const orderOffersArr = get(cart, 'orderOffers', []);
      const appliedOffers = this.getAppliedOffer({ orderOffersArr });
      const draftCart = produce(cart, (draftState) => {
        draftState.items.push({
          menuItemId: get(menuItem, 'id', ''),
          customization: get(menuItem, 'description', ''),
          quantity: 1,
          upsellItemId,
          upsellMenuItemId,
        });
        draftState['upsellSessionId'] = upsellSessionId;
      });
      this.props.actions
        .fetchViewCartItemsAction({ ...draftCart, appliedOffers })
        .then((viewCartItemsResponse) => {
          if (apiResponseCodes.SUCCESS.includes(get(viewCartItemsResponse, 'status', ''))) {
            // Hide upsell item card stack
            this.setState({
              showUpsellItemCardStack: false,
            });
          } else {
            this.setState({
              showUpsellItemCardStack: false,
            });
          }

          this.setIsCartReady(true);
        });
    } else {
      // Hide upsell item card stack
      this.setState({
        showUpsellItemCardStack: false,
      });
    }
  };

  handlePassDealCTAClick = async (itemId) => {
    const { cartUpsellItems } = this.props;
    const updateCartUpsellItemResponse = await this.props.actions.updateCartUpsellItem(
      this.createUpdateCartUpsellItemRequestBody({ cartUpsellItems, itemId }),
    );
    if (apiResponseCodes.SUCCESS.includes(get(updateCartUpsellItemResponse, 'status', ''))) {
      const { cartUpsellItems } = this.state;
      const updatedCartUpsellItems = get(updateCartUpsellItemResponse, 'data.upsellItems', []).find(
        (item) => item,
      );
      const remainingCartUpsellItems = cartUpsellItems.filter(
        (item) => item.id !== updatedCartUpsellItems.id,
      );
      if (remainingCartUpsellItems.length > 0) {
        this.setState({
          cartUpsellItems: remainingCartUpsellItems,
        });
      } else {
        this.setState({
          showUpsellItemCardStack: false,
        });
      }
    } else {
      this.setState({
        showUpsellItemCardStack: false,
      });
    }
  };

  createUpdateCartUpsellItemRequestBody = ({ cartUpsellItems, itemId }) => {
    let updateCartUpsellItemReqBody = {};
    const draftCartUpsellItems = produce(cartUpsellItems, (draftState) => {
      let selectedUpsellItem =
        draftState &&
        get(draftState, 'upsellItems', []).filter((upsellItem) => upsellItem.id === itemId);
      if (selectedUpsellItem.length > 0) {
        selectedUpsellItem[0].isSeen = true; // setting isSeen true
      }
      draftState['upsellItems'] = selectedUpsellItem;
    });
    updateCartUpsellItemReqBody = draftCartUpsellItems;
    return updateCartUpsellItemReqBody;
  };

  removeAvailedUpsellItemClick = () => {
    this.setState({
      showPopup: true,
      popupContent: {
        title: upsellConfig.removeUpsellPopup.title,
        description: upsellConfig.removeUpsellPopup.description,
        popupCTA: upsellConfig.removeUpsellPopup.popupCTA,
      },
      handlePopupCTA: () => this.handleRemoveAvailedUpsellItem(),
    });
  };

  handleRemoveAvailedUpsellItem = () => {
    const { cart } = this.state;
    const orderOffersArr = get(cart, 'orderOffers', []);
    const draftCart = produce(cart, (draftState) => {
      draftState.items = draftState.items && draftState.items.filter((item) => !item.itmSrc); // remove item with itmSrc
      draftState['appliedOffers'] = this.getAppliedOffer({ orderOffersArr });
    });
    this.props.actions.fetchViewCartItemsAction(draftCart).then((viewCartItemsResponse) => {
      if (apiResponseCodes.SUCCESS.includes(get(viewCartItemsResponse, 'status', ''))) {
        // Hide upsell item card stack
        this.setState({
          showPopup: false,
          showErrorPopup: false,
        });
      }

      this.setIsCartReady(true);
    });
  };

  getAppliedOffer = ({ orderOffersArr }) => {
    let appliedOffers = [];
    const orderOffer =
      orderOffersArr &&
      orderOffersArr.length > 0 &&
      orderOffersArr.find((orderOffer) => orderOffer);
    if (get(orderOffer, 'type', '') === offerTypeCode.ItemFree) {
      appliedOffers.push(get(orderOffer, 'offerId', ''));
      return appliedOffers;
    }

    if (get(orderOffer, 'discountAmount', 0) > 0) {
      appliedOffers.push(get(orderOffer, 'offerId', ''));
    }
    return appliedOffers;
  };

  onClosePopupComponent = () => {
    this.setState({
      showPopup: false,
      popupChildren: '',
      popupContent: '',
    });
  };

  handleAdditionalChargesInfoIconClick = () => this.setState({ showAdditionalChargesPopup: true });

  handleCloseAdditionalChargesPopup = () => this.setState({ showAdditionalChargesPopup: false });

  handleFetchGuestCashCards = async () => {
    // Bring cash card featured enabled flags from App component's SubscriptionContext
    const { cashCard: cashCardFeatureEnabled } = this.context;

    const { loginData, restaurant } = this.props;
    const guestId = get(loginData, 'id', '');

    // If cash card feature is enabled and the guest is logged-in
    if (cashCardFeatureEnabled && !!guestId) {
      // Fetch the guest's cash card, if any, to bring updated cash card info
      const restaurantId = get(restaurant, 'id', '');
      const constructedURL = `/cashCards/guests?guestId=${guestId}&restaurantIds=${restaurantId}`;
      let guestCashCardResponse = {};
      await this.props.actions.fetchGuestCashCards({ url: constructedURL }).then((response) => {
        guestCashCardResponse = response;
      });

      return guestCashCardResponse;
    }
  };

  handleTrueCallerExceptionCTA = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
    });
    this.handleLoginClick();
  };

  handleTrueCallerSignIn = ({ requestId }) => {
    this.props.actions.beginAjaxCallAction();
    this.props.actions.signInByTrueCallerAction({ requestId }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const guestLoginData = get(response, 'data', {});
        this.setState(
          {
            toast: constants.authentication.toastMessage.successfulLogin,
          },
          () => {
            this.props.actions.dispatchGuestLoginDataAction(guestLoginData);
            this.props.actions.endAjaxCallAction();
          },
        );
      } else {
        const code = get(response, 'data.code', '');
        const message = toTitleCase(get(response, 'data.message', ''));
        if (
          isEqual(code, authenticationConfig.userRejected) ||
          isEqual(code, authenticationConfig.trueCallerTimeout) ||
          isEqual(code, authenticationConfig.trueCallerRequestError)
        ) {
          let errorContent = getTrueCallerErrorContent({
            code,
            message,
          });
          this.setState(
            {
              showErrorPopup: true,
              errorContent,
              errorCode: code,
              handleErrorCTA: this.handleTrueCallerExceptionCTA,
            },
            () => {
              this.props.actions.endAjaxCallAction();
            },
          );
        } else if (isEqual(code, authenticationConfig.requestIdNotFound)) {
          // Again hit the true caller sign in handler
          this.handleTrueCallerSignIn({ requestId });
        } else {
          this.setState(
            {
              showAuthenticationPopup: true,
            },
            () => {
              this.props.actions.endAjaxCallAction();
            },
          );
        }
      }
    });
  };

  handleLoginClick = (context = '') => {
    const { appSettings, restaurantConfigAdditionalProps } = this.props;
    // first check at restaurant level then check at app level
    const isTrueCallerEnabled = get(
      restaurantConfigAdditionalProps.find((prop) => get(prop, 'code') === 'tc.isEnabled'),
      'value',
      get(appSettings, 'tc.isEnabled', 'false'),
    );
    if (JSON.parse(isTrueCallerEnabled) && !isIOS()) {
      const requestId = generateUUID();
      const trueCallerDeeplink = getTrueCallerDeepLink({ requestId });
      window.location = trueCallerDeeplink; // ? Warning here
      setTimeout(() => {
        if (document.hasFocus()) {
          // Truecaller app not present on the device and you redirect the user the regular otp flow
          this.setState({
            showAuthenticationPopup: true,
            showUpsellStackPostLogin: isEqual(context, 'upsell') ? true : false,
          });
        } else {
          // Truecaller app present on the device and the profile overlay opens
          this.handleTrueCallerSignIn({ requestId });
        }
      }, 600);
    } else {
      this.setState({
        showAuthenticationPopup: true,
        showUpsellStackPostLogin: isEqual(context, 'upsell') ? true : false,
      });
    }
  };

  handleRemoveItemAndUpsellItem = () => {
    const { cart, selectedMenuItem, removableUpsellItemIds } = this.state;
    const selectedMenuItemIndex = get(selectedMenuItem, 'index', 0);
    const orderOffersArr = get(cart, 'orderOffers', []);
    const draftCart = produce(cart, (draftState) => {
      if (selectedMenuItemIndex > -1) {
        --draftState.items[selectedMenuItemIndex].quantity;
        if (draftState.items[selectedMenuItemIndex].quantity === 0) {
          draftState.items.splice(selectedMenuItemIndex, 1);
        }
        draftState['appliedOffers'] = this.getAppliedOffer({ orderOffersArr });
      }
      // remove upsellItemId's which are included in removableUpsellItemIds arr
      draftState.items =
        draftState.items &&
        draftState.items.filter((item) => !removableUpsellItemIds.includes(item.upsellItemId));
    });
    this.props.actions.fetchViewCartItemsAction(draftCart).then((viewCartItemsResponse) => {
      if (apiResponseCodes.SUCCESS.includes(get(viewCartItemsResponse, 'status', ''))) {
        this.setState({
          showPopup: false,
        });
      }

      this.setIsCartReady(true);
    });
  };

  openMembershipPopup = (context = '') => {
    this.setState({
      showMembershipPopup: true,
      membershipPopupContext: context,
      showAuthenticationPopup: false,
      showPaymentTypePopup: false,
      showVehicleDetailsPopup: false,
      showUpdateProfilePopup: false,
    });
  };

  onCloseMembershipPopup = () => {
    this.setState({ showMembershipPopup: false, membershipPopupContext: '' });
  };

  handleMembershipPopupSubmit = () => {
    const { membershipPopupContext } = this.state;

    // If context of the Membership popup is not to simply edit the member id, then continue with cart redirection
    if (membershipPopupContext !== 'edit') this.handleCartRedirection();

    this.setState({ showMembershipPopup: false, membershipPopupContext: '' });
  };

  getMemberId = () =>
    get(
      getMemberIdFromLocalStorage(get(this.props, 'loginData.id'), this.props.restaurantCode),
      'memberId',
      '',
    );

  showCartAppliedOfferSection = (appliedOffers) => {
    const [appliedOffer] = appliedOffers;
    const offerType = get(appliedOffer, 'type', '');
    // handle deal mapped offers
    if (offerType === offerTypeCode.ItemOffer && get(appliedOffer, 'isActive', false)) {
      return false;
    }
    if ([offerTypeCode.ItemFree, offerTypeCode.ComboOffer].includes(offerType)) {
      return false;
    }
    return true;
  };

  renderOfferedItemsCard = (cartData) => {
    if (!isEmpty(get(cartData, 'orderOffers', []))) {
      const [orderOffer] = cartData.orderOffers;
      if (get(orderOffer, 'type', '') === offerTypeCode.ItemFree) {
        const freeItemName = get(orderOffer, 'description', '');

        return (
          <div className="offered-items-card">
            <div className="offered-items-card_title">{constants.card.offeredItemsCard.title}</div>
            <div className="offered-items-card-item-section">
              <div className="free-item-name">{freeItemName}</div>
              <div className="free-item-instructions">
                Please contact restaurant for your choice of free item
              </div>
            </div>
          </div>
        );
      }
    }

    const offeredItems = get(cartData, 'offeredItems', []);

    return (
      offeredItems && offeredItems.length > 0 && <OfferedItemsCard offeredItems={offeredItems} />
    );
  };

  render() {
    const {
      cart,
      showAuthenticationPopup,
      showPaymentTypePopup,
      showErrorPopup,
      errorContent,
      showUpdateProfilePopup,
      showVehicleDetailsPopup,
      disableProceedBtn,
      isTakeAway,
      showPickUpTimePopup,
      selectedPickupTime,
      showDeliveryAddressPopup,
      page,
      showSelfPickUpSection,
      appliedOffers,
      specialInstructions,
      showUpsellItemCardStack,
      showUpsellStackPostLogin,
      cartUpsellItems,
      handleErrorCTA,
      errorPopupCancellable,
      errorPopupStyleType,
      showPopup,
      popupContent,
      handlePopupCTA,
      showAdditionalChargesPopup,
      toast,
      popupChildren,
      isCartReady,
      showMembershipPopup,
    } = this.state;

    const {
      restaurant,
      selectedTable,
      restaurantConfig,
      viewCartItems,
      loginData,
      verifiedLocalities,
      serviceTypes,
      previousSelectedTable,
      selectedAddress,
      guestCashCards,
      restaurantConfigAdditionalProps,
      restaurantCode,
    } = this.props;

    const {
      offerCount,
      orderOffers,
      additionalCharges,
      showUpsell,
      items,
      isTaxInclusive,
      taxAmount,
      deliveryCharges,
      effectiveDeliveryAmount,
      deliveryDiscount,
    } = viewCartItems;

    // Bring upsell and cash card featured enabled flags from App component's SubscriptionContext
    const { upsell: upsellFeatureEnabled, cashCard: cashCardFeatureEnabled } = this.context;

    const { type, tableTitle, tableLabel } = selectedTable;
    const availablePaymentTypes = get(selectedTable, 'availablePaymentTypes', []);
    const additionalProperties = get(restaurantConfig, 'additionalProperties', []);
    const availedUpsellItems = items && items.length > 0 && items.filter((item) => item.itmSrc);

    // Upsell feature related flags
    const showUpsellButtonFlag = upsellFeatureEnabled && get(showUpsell, 'isAvailable', false);
    const showUpsellItemCardStackFlag = upsellFeatureEnabled && showUpsellItemCardStack;
    const customLoginTitle = showUpsellStackPostLogin ? upsellConfig.loginTitle : '';

    // Cash-card feature related variables
    let guestCashCardObject = {};

    if (cashCardFeatureEnabled && !isEmpty(guestCashCards)) {
      [guestCashCardObject] = guestCashCards; // Extract out singular cash card issued by the guest on the visited outlet
    }

    const additionalRestaurantProperties = get(restaurantConfig, 'additionalProperties', []);

    // Membership related variables
    const showMembershipPopupFlag = showMembershipPopup;
    const memberId = this.getMemberId();

    const orderOffer = !isEmpty(orderOffers) ? orderOffers.find((orderOffer) => orderOffer) : {};

    const isTakeAwayApplicable =
      additionalRestaurantProperties &&
      JSON.parse(
        get(
          additionalRestaurantProperties.find(
            (rAddtionalProp) => rAddtionalProp.code === 'isTakeAway',
          ),
          'value',
          'true',
        ),
      );

    return (
      <>
        <Header
          onBackClick={this.onBackClick}
          restaurant={restaurant}
          identifier={routes.containers.cart}
          tableType={type}
          label={getTableLabel(type, tableTitle, tableLabel)}
        />
        <div className="cart" id="cart-wrapper">
          <div className="cart-inner-wrapper">
            {!isEmpty(toast) && <Toast content={toast} />}
            <div className="cart-title-section">
              <div className="cart_title">{cartScreenConfig.yourOrder}</div>
              {showUpsellButtonFlag && (
                <UpsellButton
                  className={isCartReady ? 'wiggle' : ''}
                  onClick={this.handleUpsellButtonClick}
                />
              )}
            </div>
            {/* Delivery Card section */}
            {(isEqual(type, 'DL') || isEqual(get(previousSelectedTable, 'type', ''), 'DL')) && (
              <DeliveryCard
                loginData={loginData}
                page={page}
                restaurantCity={startCase(get(restaurant, 'city', constants.defaultCity))}
                verifiedLocalities={verifiedLocalities}
                type={type}
                restaurantAddress={get(restaurant, 'address', '')}
                previousSelectedTable={previousSelectedTable}
                selectedAddress={selectedAddress}
                handleLinkClick={this.handleLinkClick}
                handleGetItDeliveredClick={this.handleGetItDeliveredClick}
              />
            )}
            {isEmpty(previousSelectedTable) && isEqual(type, 'SP') && (
              <div className="cart_self-pickup-section">
                <span className="header-text">{cartScreenConfig.orderPickupText}</span>
                <span className="address">{get(restaurant, 'address', '')}</span>
              </div>
            )}
            {/* this bottom content will be scrollable */}
            {this.renderCartItems(cart)}
            {availedUpsellItems && availedUpsellItems.length > 0 && (
              <AvailedUpsellItemCard
                availedUpsellItems={availedUpsellItems}
                removeAvailedUpsellItemClick={this.removeAvailedUpsellItemClick}
              />
            )}
            {this.renderOfferedItemsCard(viewCartItems)}
            {isTakeAwayApplicable && !isEqual(type, 'DL') && (
              <div className="cart_take-away">
                <CheckBox
                  label={cartScreenConfig.takeAway}
                  name="takeAway"
                  checked={isTakeAway}
                  handleOnChange={this.onTakeAwayClick}
                />
              </div>
            )}
            <div className="cart_specialInstructions">
              <TextField
                type="text"
                name="specialInstructions"
                placeholder={cartScreenConfig.specialInstructionPlaceholder}
                value={specialInstructions}
                onChange={(event) => this.handleSpecialInstructionsChange(event)}
                onBlur={(event) => this.handleSpecialInstructionOnBlur(event)}
                autoFocus={false}
              />
            </div>
            <span className="cart_line-break-2" />
            {!isEmpty(memberId) && (
              <>
                <div className="cart_membership-details-wrapper">
                  <span className="member-id">Member ID: {memberId}</span>
                  <span className="actions edit" onClick={() => this.openMembershipPopup('edit')}>
                    <img src={editIcon} alt="EDIT" />
                  </span>
                </div>
                <span className="cart_line-break-2" />
              </>
            )}
            <div className="cart_total-section">
              <div>
                <span>
                  {cartScreenConfig.total}
                  <span className="taxes-text">
                    &nbsp;
                    {additionalCharges && additionalCharges.length > 0
                      ? cartScreenConfig.includingTaxesAndCharges
                      : cartScreenConfig.includingTaxes}
                  </span>
                </span>
                <img
                  className="info-icon"
                  src={infoIcon}
                  onClick={this.handleAdditionalChargesInfoIconClick}
                  alt=""
                />
              </div>
              <div className="total">
                <span className="amount"> {`₹ ${get(cart, 'totalAmount', 0)}`}</span>
              </div>
            </div>
          </div>

          {/* Upsell item card stack */}
          <CSSTransition
            in={showUpsellItemCardStackFlag}
            timeout={{
              enter: upsellStackMountTransitionDelay,
              exit: upsellStackMountTransitionDelay * 2,
            }}
            classNames="upsell-item-card-stack-backdrop"
            unmountOnExit
          >
            <div className="upsell-item-card-stack-backdrop"></div>
          </CSSTransition>
          <CSSTransition
            in={showUpsellItemCardStackFlag}
            timeout={upsellStackMountTransitionDelay}
            classNames="upsell-item-card-stack"
            unmountOnExit
          >
            <UpsellItemCardStack
              items={cartUpsellItems}
              handleAddToCartCTAClick={(item) => this.handleUpsellItemAddToCartCTAClick(item)}
              handlePassDealCTAClick={(itemId) => this.handlePassDealCTAClick(itemId)}
            />
          </CSSTransition>
        </div>

        {/* coupon and cart section starts here */}

        {offerCount > 0 && (
          <>
            {(orderOffers && orderOffers.length === 0) || appliedOffers.length === 0 ? (
              <div className="cart_offers-section" onClick={() => this.redirectToOffersScreen()}>
                <img src={applyOfferIcon} alt="apply-offer" />
                <span className="applyCoupon">{toTitleCase(constants.offers.applyOfferCTA)}</span>
                <span className="text">
                  {offerCount} {constants.offers.availableText}
                </span>
                <span className="icon">
                  <i className="chevron right icon" />
                </span>
              </div>
            ) : (
              this.showCartAppliedOfferSection(appliedOffers) && (
                <div className="cart_offers-section">
                  <img className="offer-icon" src={applyOfferIcon} alt="apply-offer" />
                  <span className="tag">{get(orderOffer, 'code', '')}</span>
                  <span className="discount">
                    {get(orderOffer, 'discountAmount', 0)
                      ? `${constants.offers.discountText} ₹ ${get(orderOffer, 'discountAmount', 0)}`
                      : ''}
                  </span>
                  <img
                    className="remove-offer"
                    alt="close"
                    src={closeIcon}
                    onClick={() => this.removeAppliedCouponClick()}
                  />
                </div>
              )
            )}
          </>
        )}
        <div className="cart_footer">
          <div>{`₹ ${get(cart, 'totalAmount', 0)}`}</div>
          <div className="cart_proceedButton">
            <Button
              onClick={this.onProceedClick}
              content={cartScreenConfig.proceed}
              disabled={disableProceedBtn}
            />
          </div>
        </div>
        {showAuthenticationPopup && (
          <AuthenticationPopup
            openSegmentPopup={showAuthenticationPopup}
            dismissSegmentPopup={this.onCloseAuthenticationPopup.bind(this)}
            customTitle={customLoginTitle}
          />
        )}
        {/* TODO: PaymentTypePopup is deprecated, now replaced by PaymentPopup. Cleanup state and variables related to PaymentTypePopup */}
        {/* {showPaymentTypePopup && (
          <PaymentTypePopup
            openSegmentPopup={showPaymentTypePopup}
            dismissSegmentPopup={this.onClosePaymentTypePopup.bind(this)}
            availablePaymentTypes={availablePaymentTypes}
            additionalProperties={additionalProperties}
            showPostPaidConfirmationPopup={showPostPaidConfirmationPopup}
            handlePaymentTypeClick={this.handlePaymentTypeClick.bind(this)}
          />
        )} */}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={errorPopupCancellable ? this.onCloseExceptionComponent : undefined} // Sending this prop as undefined as the SegmentComponent being used internally in ExceptionComponent implicitly handles it
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={handleErrorCTA}
          />
        )}
        {showUpdateProfilePopup && (
          <UpdateProfileComponent
            openSegmentPopup={showUpdateProfilePopup}
            dismissSegmentPopup={this.onCloseUpdateProfilePopup.bind(this)}
            vehicleId={(id) => this.getSelectedVehicle(id)}
          />
        )}
        {showVehicleDetailsPopup && (
          <VehicleDetailsComponent
            openSegmentPopup={showVehicleDetailsPopup}
            dismissSegmentPopup={this.onCloseVehicleDetailsPopup.bind(this)}
            vehicleId={(id) => this.getSelectedVehicle(id)}
            page={page}
          />
        )}
        {showPickUpTimePopup && (
          <PickupTimePopup
            openSegmentPopup={showPickUpTimePopup}
            dismissSegmentPopup={this.onClosePickupTimePopup}
            onPickupTimeChange={this.handlePickUpTimeChange}
            selectedPickupTime={selectedPickupTime}
            additionalRestaurantProperties={additionalRestaurantProperties}
            handlePickUpTimeCTA={this.handlePickUpTimeCTA}
          />
        )}
        {showDeliveryAddressPopup && (
          <DeliveryAddressPopup
            openSegmentPopup={showDeliveryAddressPopup}
            dismissSegmentPopup={this.onCloseDeliveryAddressPopup}
            loginData={loginData}
            serviceTypes={serviceTypes}
            verifiedLocalities={verifiedLocalities}
            page={page}
            showSelfPickUpSection={showSelfPickUpSection}
            handleDeliveryAddressCTA={({
              addressObj,
              isSelfPickUpSelected,
              isSavedAddressSelected,
            }) =>
              this.handleDeliveryAddressCTA({
                addressObj,
                isSelfPickUpSelected,
                isSavedAddressSelected,
              })
            }
          />
        )}
        {showPopup && (
          <Popup
            openSegmentPopup={showPopup}
            dismissSegmentPopup={this.onClosePopupComponent}
            content={popupContent}
            children={popupChildren}
            handleCTA={handlePopupCTA}
            handleSecondaryCTA={this.handleRemoveItemAndUpsellItem}
          />
        )}
        {showPaymentTypePopup && (
          <PaymentPopup
            open={showPaymentTypePopup}
            onClose={this.onClosePaymentTypePopup}
            guestCashCardInfo={guestCashCardObject}
            cashCardFeatureEnabled={cashCardFeatureEnabled}
            payableAmount={get(cart, 'totalAmount', '')}
            availablePaymentTypes={availablePaymentTypes}
            additionalProperties={additionalProperties}
            handlePaymentTypeClick={this.handlePaymentTypeClick}
            fetchGuestCashCards={this.handleFetchGuestCashCards}
          />
        )}
        {showAdditionalChargesPopup && (
          <AdditionalChargesPopup
            open={showAdditionalChargesPopup}
            onClose={this.handleCloseAdditionalChargesPopup}
            isTaxInclusive={isTaxInclusive}
            additionalCharges={additionalCharges}
            taxAmount={taxAmount}
            deliveryAmount={effectiveDeliveryAmount}
            deliveryDiscount={deliveryDiscount}
            deliveryCharges={deliveryCharges}
          />
        )}
        {showMembershipPopupFlag && (
          <MembershipPopup
            openSegmentPopup={showMembershipPopup}
            dismissSegmentPopup={this.onCloseMembershipPopup}
            onSubmit={this.handleMembershipPopupSubmit}
            isMemberIdMandatory={isMembershipIdMandatoryForRestaurant(
              restaurantConfigAdditionalProps,
            )}
            memberId={memberId}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  appSettings: get(state, 'app.masterData.appSettings', {}),
  restaurant: selectRestaurant(state),
  menuItemsMap: selectMenuItemsMap(state),
  viewCartItems: get(state, 'app.viewCart', {}),
  selectedTable: get(state, 'app.selectedTable', {}),
  loginData: get(state, 'authentication.loginData', {}),
  accessToken: get(state, 'authentication.authData.accessToken', {}),
  restaurantConfig: get(state, 'app.restaurant.restaurantConfig', {}),
  errorResponse: get(state, 'app.errorResponse', {}),
  disabledItems: get(state, 'app.disabledItems', []),
  verifiedLocalities: get(state, 'app.verifiedLocalities', []),
  serviceTypes: get(state, 'app.serviceTypes', []),
  previousSelectedTable: get(state, 'app.previousSelectedTable', {}),
  selectedAddress: get(state, 'app.selectedAddress', {}),
  cart: get(state, 'app.cart', {}),
  cartUpsellItems: get(state, 'app.cartUpsellItems', {}),
  guestCashCards: get(state, 'app.guestCashCards._embedded.guestCashCards', []),
  restaurantConfigAdditionalProps: get(
    state,
    'app.restaurant.restaurantConfig.additionalProperties',
    [],
  ),
  restaurantCode: get(state, 'app.restaurant.restaurantCode', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchViewCartItemsAction,
      placeOrderAction,
      clearApiErrorResponseAction,
      clearViewCartItems,
      updateProfileAction,
      dispatchPreviousSelectedTable: dispatchPreviousSelectedTableAction,
      fetchSelectedTable: fetchSelectedTableAction,
      dispatchSelectedAddress: dispatchSelectedAddressAction,
      dispatchUserCart: dispatchUserCartAction,
      fetchCartUpsellItems: fetchCartUpsellItemsAction,
      updateCartUpsellItem: updateCartUpsellItemAction,
      fetchGuestCashCards: fetchGuestCashCardsAction,
      beginAjaxCallAction,
      endAjaxCallAction,
      signInByTrueCallerAction,
      dispatchGuestLoginDataAction,
      createCashCardRecharge: createCashCardRechargeAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
